import React, {useEffect, useRef} from 'react';
import {Menu, MenuButton, MenuItems, MenuLink, MenuPopover} from '@reach/menu-button';
import styled from 'styled-components';
import '@reach/menu-button/styles.css';
import {FormattedMessage} from 'react-intl';
import {Link} from '../../Link';
import {SlimIcon} from '../../icon/SlimIcon';
import classNames from 'classnames';
import {Caret} from '../../icon/Caret';
import {localeToPath} from '../../../utils/localeToPath';
import type {LocaleEnum} from '../../../types/locale';
import {NavigationType, SubmenuItemType} from '../../../types/block';
import {forceTrailingSlash} from '../../../utils/urlUtils';
import {IconName} from '../../icon/SvgIcon';
import {Colors} from '../../../types/colorTypes';

type StyledMenuButtonProps = {
  hasSidebar?: boolean;
  // the children prop is necessary to avoid the collision between children being optional vs mandatory in the types
  children: React.ReactNode;
  className?: string;
  bg?: string;
  text_color?: string;
  action_text_color?: string;
  is_expanded: string;
};

const MenuWrapper = styled.div`
  box-shadow: 0 2px 30px -5px rgba(36, 50, 66, 0.075);
  top: 70px;
`;

const StyledMenuButton = styled(MenuButton)<StyledMenuButtonProps>`
  padding: 0 0.75rem;
  width: 100%;
  background: ${props => getBgColor(props.is_expanded === 'true', props.bg)};
  z-index: 99;

  span {
    font-family: Open Sans, sans-serif !important;
  }

  &:hover,
  &:focus,
  &:active {
    background: ${props => (props.bg ? `${props.bg}99` : 'transparent')};
  }

  &:hover span,
  &:focus span,
  &:active span,
  &:hover svg,
  &:focus svg,
  &:active svg {
    color: ${props => (props.action_text_color ? props.action_text_color : '#1493be')} !important;
  }

  span,
  svg {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    color: ${props => getTextColor(props.is_expanded === 'true', props.text_color, props.action_text_color)} !important;
  }
`;

export const getBgColor = (isExpanded: boolean, bg?: string) => {
  if (bg) {
    return isExpanded ? `${bg}99` : bg;
  }
  return 'transparent';
};

export const getTextColor = (isExpanded: boolean, textColor?: string, actionTextColor?: string) => {
  if (textColor) {
    //Custom color from Dato
    return isExpanded ? actionTextColor : textColor;
  }
  return isExpanded ? '#1493be' : '#000000'; // Default behaviour
};

type MenuProps = Pick<NavigationType, 'submenu' | 'title'> & {
  dataTestId: string;
  linkClasses: string;
  overrideTextColor: string;
  bg?: string;
  textColor?: string;
  actionTextColor?: string;
  locale: LocaleEnum;
};

export const SubMenu = (props: MenuProps) => {
  const isExpandedRef = useRef(false);
  const subMenuItemClasses = `block p-0 bg-transparent text-black
  hover:text-primary hover:-translate-y-2
  focus:text-primary focus:-translate-y-2
  data-[selected]:text-primary data-[selected]:-translate-y-2
  active:text-primary 
  transition-all duration-[0.2s] ease-[cubic-bezier(0.4, 0, 0.2, 1)]`;

  useEffect(() => {
    // hide submenu on scroll by transforming it into mousedown event on document
    const listenToScroll = () => isExpandedRef.current && document.dispatchEvent(new MouseEvent('mousedown'));

    window.addEventListener('scroll', listenToScroll, {passive: true});

    return () => {
      window.removeEventListener('scroll', listenToScroll);
    };
  }, []);

  const submenuDataTestId = `${props.dataTestId}-submenu`;

  return (
    <Menu>
      {({isExpanded}) => {
        isExpandedRef.current = isExpanded;

        return (
          <>
            <StyledMenuButton
              className={props.linkClasses}
              text_color={props.textColor}
              action_text_color={props.actionTextColor}
              bg={props.bg}
              is_expanded={isExpanded.toString()}
              data-testid={props.dataTestId}
            >
              <span className={classNames('font-body mr-2px')}>{props.title}</span>
              <Caret isExpanded={isExpanded} />
            </StyledMenuButton>
            <MenuPopover className='z-9 left-0 w-full' data-testid={`${submenuDataTestId}-container`}>
              <MenuWrapper className='animate-slideindownsmall flex justify-center z-9 left-0'>
                <MenuItems className='w-full border-none bg-white'>
                  <div className='container max-w-screen-xl pt-2 pb-5' data-testid={submenuDataTestId}>
                    <div className='grid grid-cols-4 gap-10'>
                      {props.submenu &&
                        props.submenu.map((entry, index) => (
                          <MenuLink
                            key={`${entry.title}-${index}`}
                            to={getLink(props.locale as LocaleEnum, entry)}
                            as={Link}
                            className={subMenuItemClasses}
                            data-testid={`${entry.title}-${index}`}
                          >
                            <p className='flex align-middle py-5 m-0'>
                              <SlimIcon className='text-inherit' color={Colors.GREY} size='small' name={entry.icon as IconName} />
                              <span className='ml-3 mt-1 mb-0 whitespace-normal font-body font-semibold text-base'>{entry.title}</span>
                            </p>
                            {entry.description && <p className='mt-0 font-body whitespace-normal text-sm text-grey'>{entry.description}</p>}
                            {entry.isNew && (
                              <span className='text-white bg-primary text-xs font-bold rounded-full py-1 px-3'>
                                <FormattedMessage id='header.menu.new' />
                              </span>
                            )}
                          </MenuLink>
                        ))}
                    </div>
                  </div>
                </MenuItems>
              </MenuWrapper>
            </MenuPopover>
          </>
        );
      }}
    </Menu>
  );
};

export const getLink = (locale: LocaleEnum, entry: SubmenuItemType): string => {
  const slug = entry.slug ? entry.slug.slug : entry.customSlug;
  const model = entry.slug ? (entry.slug.model.name as 'BlogPost' | 'Page') : undefined;

  if (model !== undefined) {
    return `/${localeToPath(locale)}${model === 'BlogPost' ? '/post/' : '/'}${forceTrailingSlash(slug)}`;
  }

  if (!slug.startsWith('http')) {
    return `/${localeToPath(locale)}/${forceTrailingSlash(slug)}`;
  }

  return forceTrailingSlash(slug);
};
